import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { toast } from 'sonner';
import { getContextualApiErrorMessage, redirectToLogin, handleApiError } from '@/lib/utils';
import { getAttributionHeaders } from '@/lib/utm';
import { getStoredAuthToken } from '@/lib/authStorage';

declare module 'axios' {
  export interface AxiosRequestConfig {
    _skipAuthCheck?: boolean;
    _suppressErrorToast?: boolean;
    _skipChannelSourceHeader?: boolean;
  }
}

const api = axios.create({
  baseURL: import.meta.env.DEV ? '' : import.meta.env.VITE_API_BASE_URL,
  timeout: 0,
  headers: {
    'Content-Type': 'application/json',
  },
});

const getApiKey = (url?: string) => url?.split('/').filter(Boolean)[0];

const normalizeHeaderValue = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text || undefined;
};

const isSuccessErrorCode = (errorCode: unknown) =>
  errorCode === undefined || errorCode === null || errorCode === '' || String(errorCode) === '0';

const getApiMessage = (value: unknown, fallback: string) => {
  const message = typeof value === 'string' ? value.trim() : '';
  return message || fallback;
};

const hasResponseWrapper = (
  payload: unknown,
): payload is {
  data?: unknown;
  errorCode?: unknown;
  value?: unknown;
} => {
  if (!payload || typeof payload !== 'object') return false;
  if ('errorCode' in payload || 'value' in payload) return true;
  return 'data' in payload && Object.keys(payload).length === 1;
};

type ApiErrorDetails = {
  status?: number;
  errorCode?: unknown;
  value?: unknown;
  data?: unknown;
  payload?: unknown;
  url?: string;
  method?: string;
};

const createDetailedApiError = (message: string, details: ApiErrorDetails) => {
  const error = new Error(message) as Error & { apiError?: ApiErrorDetails };
  error.apiError = details;
  return error;
};

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getStoredAuthToken();

    if (config._skipAuthCheck !== true && !token) {
      toast.error('Please log in first', { id: 'auth-required' });
      redirectToLogin();
      return Promise.reject(new Error('Authentication required'));
    }

    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      config.headers['token'] = token;
    }

    const attributionHeaders = getAttributionHeaders();
    const normalizedAttributionHeaderEntries = Object.entries(attributionHeaders)
      .filter(([key]) => key !== 'Channel-Source')
      .map(([key, value]) => [key, normalizeHeaderValue(value)] as const)
      .filter((entry): entry is readonly [string, string] => Boolean(entry[1]));

    if (normalizedAttributionHeaderEntries.length > 0) {
      normalizedAttributionHeaderEntries.forEach(([key, value]) => {
        config.headers[key] = value;
      });
    }

    const channelSource =
      normalizeHeaderValue(attributionHeaders['Channel-Source']) ||
      normalizeHeaderValue(import.meta.env.VITE_CHANNEL_SOURCE);
    const existingChannelSource =
      config.headers['Channel-Source'] || config.headers['channel-source'];
    if (channelSource && config._skipChannelSourceHeader !== true && !existingChannelSource) {
      config.headers['Channel-Source'] = channelSource;
    }

    const isFormDataRequest = typeof FormData !== 'undefined' && config.data instanceof FormData;

    if (isFormDataRequest) {
      const headers = config.headers as Record<string, unknown> & {
        delete?: (name: string) => void;
      };

      if (typeof headers.delete === 'function') {
        headers.delete('Content-Type');
        headers.delete('content-type');
      }

      delete headers['Content-Type'];
      delete headers['content-type'];
    } else if (!config.headers['Content-Type']) {
      config.headers['Content-Type'] = 'application/json';
    }

    return config;
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => {
    const status = response?.status ?? 500;

    if (status === 200) {
      const responseData = response?.data ?? null;
      if (!hasResponseWrapper(responseData)) return responseData;

      const { data = null, errorCode = 0, value } = responseData;

      if (!isSuccessErrorCode(errorCode)) {
        const errorMsg = getContextualApiErrorMessage(getApiMessage(value, 'Operation failed'), {
          requestUrl: response.config?.url,
          requestData: response.config?.data,
        });
        const key = getApiKey(response.config?.url);
        const suppress = response.config?._suppressErrorToast;

        if (!suppress) {
          console.error(`[business] Service error [${errorCode}]: ${errorMsg}`);
        }

        if (key === 'lowcode-business-growthtrack' && !suppress) {
          toast.error(`${errorMsg}:${errorCode}`);
        } else if (!suppress) {
          toast.error(errorMsg);
        }

        return Promise.reject(
          createDetailedApiError(errorMsg, {
            status,
            errorCode,
            value,
            data,
            payload: responseData,
            url: response.config?.url,
            method: response.config?.method,
          }),
        );
      }

      return data;
    }

    const errorMsg = getContextualApiErrorMessage(
      getApiMessage(response?.data?.value, `Request failed (${status})`),
      {
        requestUrl: response.config?.url,
        requestData: response.config?.data,
      },
    );
    toast.error(errorMsg);
    console.error(`[http] Request failed [${status}]: ${errorMsg}`);
    return Promise.reject(
      createDetailedApiError(errorMsg, {
        status,
        value: response?.data?.value,
        data: response?.data?.data,
        payload: response?.data,
        url: response.config?.url,
        method: response.config?.method,
      }),
    );
  },
  (error: AxiosError) => {
    let message = 'Network error, please check your connection';
    const key = getApiKey(error.config?.url);
    const detailedError = error as AxiosError & { apiError?: ApiErrorDetails };

    if (error.response) {
      const { status } = error.response;
      const data = (error.response.data as Record<string, unknown>) || {};
      const translatedMessage = getContextualApiErrorMessage(
        getApiMessage(data?.value, error.message),
        {
          requestUrl: error.config?.url,
          requestData: error.config?.data,
        },
      );

      if (key === 'lowcode-business-growthtrack') {
        const errorCode = data.errorCode as string | number;
        message = handleApiError(errorCode, translatedMessage, error.config?._skipAuthCheck);
      } else {
        message = handleApiError(status, translatedMessage, error.config?._skipAuthCheck);
      }
      detailedError.apiError = {
        status,
        errorCode: data.errorCode,
        value: data.value,
        data: data.data,
        payload: data,
        url: error.config?.url,
        method: error.config?.method,
      };
    } else if (error.message === 'Authentication required') {
      return Promise.reject(error);
    }

    console.error(`HTTP error: ${message}`);
    return Promise.reject(error);
  },
);

export default api;
